/**
 * Zod validation schemas for user management forms.
 */

import { z } from 'zod';

/**
 * Base schema for user form fields (shared between create and update).
 * Note: expiry_date is stored as YYYY-MM-DD string in the form (for date input),
 * but will be converted to ISO date-time string when sending to API.
 */
const baseUserFormSchema = z.object({
  email: z.string().min(1, 'Email is required').pipe(z.email('Please enter a valid email address')),
  name: z.string().optional(),
  is_admin: z.boolean().default(false),
  expiry_date: z
    .string()
    .optional()
    .refine(
      (val) => {
        if (!val || val === '') return true; // Optional field
        // Validate date format (YYYY-MM-DD)
        return /^\d{4}-\d{2}-\d{2}$/.test(val);
      },
      { message: 'Date must be in YYYY-MM-DD format' },
    ),
});

/**
 * Schema for creating a new user.
 * Password is required for new users.
 */
export const createUserSchema = baseUserFormSchema.extend({
  password: z.string().min(1, 'Password is required').min(8, 'Password must be at least 8 characters'),
});

/** Field-values (input) shape: `is_admin` may be absent before its `.default(false)` applies. */
export type CreateUserFormValues = z.input<typeof createUserSchema>;
/** Validated (output) shape passed to the submit handler. */
export type CreateUserFormData = z.output<typeof createUserSchema>;

/**
 * Schema for updating an existing user.
 * Password is optional (leave empty to keep current password).
 * If provided, must be at least 8 characters long.
 */
export const updateUserSchema = baseUserFormSchema.extend({
  password: z
    .string()
    .optional()
    .refine((val) => val === undefined || val === '' || val.length >= 8, {
      message: 'Password must be at least 8 characters',
    }),
});

/** Field-values (input) shape: `is_admin` may be absent before its `.default(false)` applies. */
export type UpdateUserFormValues = z.input<typeof updateUserSchema>;
/** Validated (output) shape passed to the submit handler. */
export type UpdateUserFormData = z.output<typeof updateUserSchema>;
